import React, { memo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Search from '@mui/icons-material/Search';
import SearchOff from '@mui/icons-material/SearchOff';
import UploadFile from '@mui/icons-material/UploadFile';
import { SwaggerFile } from '../../types/swaggerFile';
import SwaggerFileRow, { SwaggerFileRowHandlers } from './SwaggerFileRow';
import { FILE_GRID_SX } from './gridLayout';

const COLUMNS = ['File', 'Tags', 'Size', 'Uploaded', ''];

interface SwaggerFilesPanelProps extends SwaggerFileRowHandlers {
  totalCount: number;
  visibleFiles: SwaggerFile[];
  loading: boolean;
  loadError: string | null;
  query: string;
  newFileId: string | null;
  downloadingId: string | null;
  onQueryChange: (value: string) => void;
  onRetry: () => void;
}

const SearchField: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => (
  <Box sx={{ ml: 'auto', flex: '0 1 320px', display: 'flex', alignItems: 'center', gap: 1, height: 36, px: 1.5, borderRadius: 4.5, bgcolor: 'action.hover' }}>
    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search name, description or tag"
      inputProps={{ 'aria-label': 'Search swagger files' }}
      sx={{ flex: 1, minWidth: 0, fontSize: 13 }}
    />
  </Box>
);

const EmptyState: React.FC<{ filtered: boolean }> = ({ filtered }) => {
  const Icon = filtered ? SearchOff : UploadFile;
  return (
    <Box sx={{ py: 6, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
      <Icon sx={{ fontSize: 36, color: 'text.disabled' }} />
      <Typography sx={{ fontWeight: 600 }}>{filtered ? 'No files match' : 'No Swagger files yet'}</Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
        {filtered ? 'Try a different search.' : 'Upload a spec to start generating mapping documents.'}
      </Typography>
    </Box>
  );
};

/** The files card: count + search toolbar above a grid table that scrolls horizontally on narrow screens. */
const SwaggerFilesPanel: React.FC<SwaggerFilesPanelProps> = ({
  totalCount,
  visibleFiles,
  loading,
  loadError,
  query,
  newFileId,
  downloadingId,
  onQueryChange,
  onRetry,
  ...rowHandlers
}) => {
  const showEmpty = !loading && !loadError && visibleFiles.length === 0;
  return (
    <Paper variant="outlined" component="section" aria-label="Swagger files" sx={{ borderRadius: 4, overflow: 'hidden' }}>
      <Box sx={{ minHeight: 56, display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, pl: 2.5, pr: 2, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
          Files
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {totalCount} {totalCount === 1 ? 'file' : 'files'}
        </Typography>
        <SearchField value={query} onChange={onQueryChange} />
      </Box>
      {loading ? <LinearProgress aria-label="Loading swagger files" /> : null}
      {loadError ? (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : null}
      <Box sx={{ overflowX: 'auto' }}>
        <Box role="table" aria-label="Swagger files" sx={{ minWidth: 900 }}>
          <Box role="row" sx={{ ...FILE_GRID_SX, height: 44, bgcolor: 'action.hover', fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
            {COLUMNS.map((label, i) => (
              <Box role="columnheader" component="span" key={label || i}>
                {label}
              </Box>
            ))}
          </Box>
          {visibleFiles.map((file) => (
            <SwaggerFileRow key={file.id} file={file} isNew={file.id === newFileId} downloading={file.id === downloadingId} {...rowHandlers} />
          ))}
          {showEmpty ? <EmptyState filtered={totalCount > 0} /> : null}
        </Box>
      </Box>
    </Paper>
  );
};

export default memo(SwaggerFilesPanel);
